import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState, Spinner } from '@/components/common';
import { toast } from 'sonner';
import { Brain, Send, Trash2, PackagePlus, Sparkles } from 'lucide-react';

export default function BrainDump() {
  const [notes, setNotes] = useState(null);
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(null);
  const [ideas, setIdeas] = useState(null);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get('/notes');
    setNotes(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api.post('/notes', { content: content.trim() });
      setContent('');
      toast.success('Captured');
      load();
    } catch (e) {
      toast.error('Failed to save note');
    } finally { setBusy(false); }
  };

  const convert = async (note) => {
    try {
      const { data } = await api.post(`/notes/${note.id}/convert`, {});
      toast.success(`Product created: ${data.name}`);
      navigate(`/products/${data.id}`);
    } catch (e) {
      toast.error('Convert failed');
    }
  };

  const extract = async (note) => {
    setExtracting(note.id);
    try {
      const { data } = await api.post(`/notes/${note.id}/extract-ideas`);
      setIdeas({ note, list: data.ideas || [] });
      setIdeasOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI extraction failed');
    } finally { setExtracting(null); }
  };

  const createFromIdea = async (idea) => {
    try {
      const { data } = await api.post('/products', { name: idea.name, description: idea.description, stage: 'product_idea', auto_score: false });
      toast.success(`Product created: ${data.name}`);
      navigate(`/products/${data.id}`);
    } catch (e) {
      toast.error('Failed to create product');
    }
  };

  const del = async (id) => {
    await api.delete(`/notes/${id}`);
    load();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Brain Dump</h1>
        <div className="page-accent-bar" />
        <p className="mt-2 text-sm text-muted-foreground">Zero-friction capture. Ideas, products, angles, random thoughts — dump it all here, filter later.</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <Textarea data-testid="brain-dump-quick-capture-textarea" value={content} onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }}
            placeholder="e.g. Saw a viral TikTok about a posture corrector for gamers... could target desk workers too. Angle: 'your setup is destroying your spine'"
            rows={4} className="resize-none border-0 p-0 shadow-none focus-visible:ring-0" />
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Ctrl/Cmd + Enter to save</p>
            <Button data-testid="brain-dump-save-button" size="sm" onClick={submit} disabled={busy || !content.trim()} className="accent-btn gap-2">
              {busy ? <Spinner /> : <Send className="h-4 w-4" />} Capture
            </Button>
          </div>
        </CardContent>
      </Card>

      {notes === null ? (
        <div className="flex justify-center py-10"><Spinner className="h-6 w-6" /></div>
      ) : notes.length === 0 ? (
        <EmptyState testId="brain-dump-empty" icon={Brain} title="Nothing captured yet" subtitle="Your best product tests start as messy notes. Dump your first idea above." />
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <Card key={n.id} data-testid="brain-dump-note-card" className={n.converted ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <p className="whitespace-pre-wrap text-sm">{n.content}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                  {n.converted && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">CONVERTED</span>}
                  <div className="flex-1" />
                  {!n.converted && (
                    <>
                      <Button data-testid="brain-dump-extract-button" variant="outline" size="sm" onClick={() => extract(n)} disabled={extracting === n.id} className="gap-1.5">
                        {extracting === n.id ? <Spinner /> : <Sparkles className="h-3.5 w-3.5" />} AI: Extract ideas
                      </Button>
                      <Button data-testid="brain-dump-convert-button" variant="outline" size="sm" onClick={() => convert(n)} className="gap-1.5">
                        <PackagePlus className="h-3.5 w-3.5" /> To Product
                      </Button>
                    </>
                  )}
                  <Button data-testid="brain-dump-delete-button" variant="ghost" size="icon" aria-label="Delete note" onClick={() => del(n.id)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={ideasOpen} onOpenChange={setIdeasOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>AI-Extracted Product Ideas</DialogTitle></DialogHeader>
          {ideas?.list?.length === 0 ? (
            <p className="text-sm text-muted-foreground">The AI didn't find a concrete product idea in this note. Add more specifics (what the product is, who it's for) and try again.</p>
          ) : (
            <div className="space-y-3">
              {ideas?.list?.map((idea, i) => (
                <div key={i} data-testid="extracted-idea-card" className="flex items-start justify-between gap-3 rounded-lg border bg-muted/40 p-3">
                  <div>
                    <p className="text-sm font-semibold">{idea.name}</p>
                    <p className="text-xs text-muted-foreground">{idea.description}</p>
                  </div>
                  <Button data-testid="extracted-idea-create-button" size="sm" onClick={() => createFromIdea(idea)}>Create</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
